import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { pushDataLayerEvent, takePendingEvent } from "@/lib/datalayer";
import { Mail, PhoneCall, MessagesSquare } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ProzessTimeline } from "@/components/buergermeisterwahl/ProzessTimeline";
import { CasesSection } from "@/components/cases/CasesSection";

export const Route = createFileRoute("/kontakt/danke")({
  head: () => ({
    meta: [
      { title: "Vielen Dank | creategy" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Ihre Anfrage ist bei uns eingegangen. Wir melden uns an Werktagen innerhalb von 24 Stunden persönlich bei Ihnen.",
      },
      { property: "og:title", content: "Vielen Dank | creategy" },
      {
        property: "og:description",
        content:
          "Ihre Anfrage ist bei uns eingegangen. Wir melden uns an Werktagen innerhalb von 24 Stunden persönlich bei Ihnen.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/kontakt/danke" }],
  }),
  component: DankePage,
});

const STEPS = [
  {
    title: "Anfrage eingegangen",
    text: "Ihre Nachricht liegt uns vor und wird direkt dem richtigen Ansprechpartner zugeordnet.",
    Icon: Mail,
  },
  {
    title: "Persönliche Rückmeldung",
    text: "Wir melden uns telefonisch oder per Mail bei Ihnen, um Ihr Anliegen kurz zu besprechen.",
    Icon: PhoneCall,
  },
  {
    title: "Kostenloses Erstgespräch",
    text: "Gemeinsam schauen wir auf Ihre Ausgangslage und mögliche nächste Schritte.",
    Icon: MessagesSquare,
  },
];

function DankePage() {
  // Nur nach tatsächlichem Absenden, einmal; direkter Aufruf und Neuladen zählen nicht.
  useEffect(() => {
    if (takePendingEvent("kontaktformular_gesendet")) {
      pushDataLayerEvent({ event: "kontaktformular_gesendet" });
    }
  }, []);

  return (
    <Layout>
      {/* Sektion 1: Hero */}
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1 text-primary">
            Vielen Dank für Ihre Anfrage.
          </h1>
          <p className="mt-8 max-w-2xl font-body text-base leading-relaxed text-foreground md:text-lg">
            Ihre Nachricht ist bei uns eingegangen. Wir melden uns an Werktagen
            innerhalb von 24 Stunden persönlich bei Ihnen.
          </p>
        </div>
      </section>

      {/* Sektion 2: Prozess */}
      <section aria-labelledby="danke-prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="danke-prozess" className="heading-h2 text-primary">
              In drei Schritten zur Zusammenarbeit.
            </h2>
          </Reveal>
          <div className="mt-16 md:mt-20">
            <ProzessTimeline headingColor="text-primary" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Sektion 3: Cases */}
      <CasesSection
        excludeTags={["buergermeisterwahl", "direktkandidaten"]}
        title={"Projekte &\u00a0Referenzen"}
        description="Ein Auszug unserer Projekte."
        headingColor="text-primary"
      />
    </Layout>
  );
}
